import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import SplashPage from "@/pages/SplashPage";
import HomePage from "@/pages/HomePage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "@/pages/AuthPages";
import LegalPage from "@/pages/LegalPage";
import DropDetailPage from "@/pages/DropDetailPage";
import WinnersPage from "@/pages/WinnersPage";
import VotePage from "@/pages/VotePage";
import { SponsorsPage, SponsorProfilePage, SponsorApplyPage } from "@/pages/SponsorPages";
import HowItWorksPage from "@/pages/HowItWorksPage";
import ProfilePage from "@/pages/ProfilePage";
import HunterProfilePage from "@/pages/HunterProfilePage";
import NotificationsPage from "@/pages/NotificationsPage";
import NotifyPage from "@/pages/NotifyPage";
import TheHollerPage from "@/pages/TheHollerPage";
import StashPage from "@/pages/StashPage";
import MyPrizesPage from "@/pages/MyPrizesPage";
import BazaarPage from "@/pages/BazaarPage";
import AuctionsPage from "@/pages/AuctionsPage";
import VaultPage, { VaultsListPage } from "@/pages/VaultPage";
import VaultQrLanding from "@/pages/VaultQrLanding";
import BadgesPage, { PublicUserBadges } from "@/pages/BadgesPage";
import PulsePage from "@/pages/PulsePage";
import MetTheFounderPage from "@/pages/MetTheFounderPage";
import SpoilsPage, { SpoilsDetailPage, SpoilsOrderPage } from "@/pages/SpoilsPage";
import AdminPage from "@/pages/AdminPage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import SponsorCheckoutSuccessPage from "@/pages/SponsorCheckoutSuccessPage";
import GoldenDropsPage from "@/pages/GoldenDropsPage";
import CityLandingPage from "@/pages/CityLandingPage";
import DropsListPage from "@/pages/DropsListPage";
import WaymarksListPage from "@/pages/WaymarksListPage";
import WaymarkDetailPage from "@/pages/WaymarkDetailPage";
import ClaimPage from "@/pages/ClaimPage";
import AboutPage from "@/pages/AboutPage";
import {
  LootQuestLandingPage,
  LootQuestDetailPage,
  LootQuestClaimPage,
  LootQuestWinnersPage,
} from "@/pages/LootQuestPages";
import { PrivateLandingPage, PrivateEventPage, PrivateSalesPage } from "@/pages/PrivatePages";
import ContactPage from "@/pages/ContactPage";
import LootMasterPage from "@/pages/LootMasterPage";
import LootMasterPortalPage from "@/pages/LootMasterPortalPage";
import SponsorMissionPage from "@/pages/SponsorMissionPage";
import MissionsPage from "@/pages/MissionsPage";
import CommunityTankPage from "@/pages/CommunityTankPage";
import GoodDeedsPage from "@/pages/GoodDeedsPage";
import { usePresenceHeartbeat } from "@/lib/presence";

function PresenceMount() {
  usePresenceHeartbeat();
  return null;
}

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="p-10 text-[#F5F2E8]/60">Loading...</div>;
  if (!user) {
    // Preserve the path the user was trying to reach so /login can bounce
    // them back after a successful sign-in.
    const next = `${location.pathname}${location.search || ""}`;
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  // Admin-gated routes also accept lootmasters — backend enforces what they
  // can actually do. Frontend just lets them onto the same console.
  if (admin && user.role !== "admin" && user.role !== "lootmaster") return <Navigate to="/home" replace />;
  return children;
}

function Wrapped({ children }) {
  return <Layout>{children}</Layout>;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
          <PresenceMount />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#0F0F0F",
                border: "1px solid #76B82A",
                color: "#F5F2E8",
                fontFamily: "JetBrains Mono, monospace",
              },
            }}
          />
          <Routes>
            <Route path="/" element={<SplashPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/safety" element={<LegalPage />} />

            <Route path="/home" element={<Wrapped><HomePage /></Wrapped>} />
            <Route path="/drops" element={<Wrapped><DropsListPage /></Wrapped>} />
            <Route path="/drops/:id" element={<Wrapped><DropDetailPage /></Wrapped>} />
            <Route path="/waymarks" element={<Wrapped><WaymarksListPage /></Wrapped>} />
            <Route path="/waymarks/:id" element={<Wrapped><WaymarkDetailPage /></Wrapped>} />
            <Route path="/claim" element={<Wrapped><ClaimPage /></Wrapped>} />
            <Route path="/winners" element={<Wrapped><WinnersPage /></Wrapped>} />
            <Route path="/vote" element={<Wrapped><VotePage /></Wrapped>} />
            <Route path="/sponsors" element={<Wrapped><SponsorsPage /></Wrapped>} />
            <Route path="/sponsors/:id" element={<Wrapped><SponsorProfilePage /></Wrapped>} />
            <Route path="/sponsor-apply" element={<Wrapped><SponsorApplyPage /></Wrapped>} />
            <Route path="/sponsor-checkout/success" element={<Wrapped><SponsorCheckoutSuccessPage /></Wrapped>} />
            <Route path="/leaderboard" element={<Wrapped><LeaderboardPage /></Wrapped>} />
            <Route path="/golden" element={<Wrapped><GoldenDropsPage /></Wrapped>} />
            <Route path="/hunts/:city" element={<Wrapped><CityLandingPage /></Wrapped>} />
            <Route path="/how-it-works" element={<Wrapped><HowItWorksPage /></Wrapped>} />
            <Route path="/about" element={<Wrapped><AboutPage /></Wrapped>} />
            <Route path="/loot-quest" element={<Wrapped><LootQuestLandingPage /></Wrapped>} />
            <Route path="/loot-quest/claim" element={<Wrapped><LootQuestClaimPage /></Wrapped>} />
            <Route path="/loot-quest/winners" element={<Wrapped><LootQuestWinnersPage /></Wrapped>} />
            <Route path="/loot-quest/:id" element={<Wrapped><LootQuestDetailPage /></Wrapped>} />
            <Route path="/notifications" element={<Wrapped><NotificationsPage /></Wrapped>} />
            <Route path="/notify" element={<Wrapped><NotifyPage /></Wrapped>} />
            <Route path="/holler" element={<Wrapped><TheHollerPage /></Wrapped>} />
            <Route path="/stash" element={<Wrapped><StashPage /></Wrapped>} />
            <Route path="/my-prizes" element={<Wrapped><MyPrizesPage /></Wrapped>} />
            <Route path="/bazaar" element={<Wrapped><BazaarPage /></Wrapped>} />
            <Route path="/auctions" element={<Wrapped><AuctionsPage /></Wrapped>} />
            <Route path="/vault" element={<Wrapped><VaultsListPage /></Wrapped>} />
            <Route path="/vault/qr/:token" element={<Wrapped><VaultQrLanding /></Wrapped>} />
            <Route path="/vault/:id" element={<Wrapped><VaultPage /></Wrapped>} />
            <Route path="/badges" element={<Wrapped><BadgesPage /></Wrapped>} />
            <Route path="/u/:username/badges" element={<Wrapped><PublicUserBadges /></Wrapped>} />
            <Route path="/pulse" element={<Wrapped><PulsePage /></Wrapped>} />
            <Route path="/met-the-founder" element={<Wrapped><MetTheFounderPage /></Wrapped>} />
            <Route path="/spoils" element={<Wrapped><SpoilsPage /></Wrapped>} />
            <Route path="/spoils/order/:token" element={<Wrapped><SpoilsOrderPage /></Wrapped>} />
            <Route path="/spoils/:slug" element={<Wrapped><SpoilsDetailPage /></Wrapped>} />

            <Route path="/profile" element={<Protected><Wrapped><ProfilePage /></Wrapped></Protected>} />
            <Route path="/u/:username" element={<Wrapped><HunterProfilePage /></Wrapped>} />
            <Route path="/private" element={<Protected><Wrapped><PrivateLandingPage /></Wrapped></Protected>} />
            <Route path="/private/sales" element={<Wrapped><PrivateSalesPage /></Wrapped>} />
            <Route path="/contact" element={<Wrapped><ContactPage /></Wrapped>} />
            <Route path="/lootmaster" element={<LootMasterPage />} />
            <Route path="/lootmaster/portal" element={<Protected><LootMasterPortalPage /></Protected>} />
            <Route path="/sponsor-a-mission" element={<SponsorMissionPage />} />
            <Route path="/missions" element={<MissionsPage />} />
            <Route path="/community-tank" element={<CommunityTankPage />} />
            <Route path="/good-deeds" element={<GoodDeedsPage />} />
            <Route path="/private/redeem/:code" element={<Protected><Wrapped><PrivateLandingPage /></Wrapped></Protected>} />
            <Route path="/private/event/:dropId" element={<Protected><Wrapped><PrivateEventPage /></Wrapped></Protected>} />
            <Route path="/admin" element={<Protected admin><Wrapped><AdminPage /></Wrapped></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
